import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type {
  WalkthroughSession,
  WalkthroughSessionEntity,
} from '@unifyapps/defs/types/walkthroughSession';
import { useTranslation } from '@unifyapps/i18n/client';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useCallback } from 'react';
import { useUserStore } from '../../../auth/UserStoreProvider';
import {
  useWalkthroughManagerStore,
  WalkthroughMode,
  WalkthroughProcessState,
} from '../../stores/WalkthroughManagerStore';
import { executePreEndHook, executePreStartHook } from '../utils/lifecycleHooks';
import { useWalkthroughSessionClient } from './useWalkthroughSessionClient';
import { useWalkthroughSessionSync } from './useWalkthroughSessionSync';

/**
 * Hook to orchestrate the lifecycle and state of a walkthrough,
 * including actions to start, stop, pause, resume, move between steps,
 * and react to page events. The concept of "session" (and backend state sync)
 * only applies when in "RUNNER" mode—this is the only mode where real user progress is persisted!
 * In PREVIEW or BUILDER, everything is local, so session management is quietly ignored.
 *
 * Use these orchestration helpers for consistent walkthrough UX wherever you want them,
 * and invite user progress to be magical when it matters.
 */
export function useWalkthroughOrchestrator() {
  const store = useWalkthroughManagerStore();
  const { createSession } = useWalkthroughSessionClient();
  const {
    saveSessionToBackend,
    notifyWalkthroughChange,
    setActiveSession,
    walkthroughInstanceEventBus,
  } = useWalkthroughSessionSync();

  const {
    transitionTo,
    stopWalkthrough: _stopWalkthrough,
    move,
    goToStep: _goToStep,
    prepareWalkthrough,
    activateWalkthrough,
    startFetchingWalkthroughs,
  } = store.getState().conductorActions;

  const currentUserDetails = useUserStore().hooks.useCurrentUserDetails();
  const isAnonymous = currentUserDetails?.external;

  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['interfaces-matrix']);

  // Surface a non-blocking error when a session fails to persist, so the user
  // knows their walkthrough progress wasn't saved (silent failures otherwise
  // make the walkthrough appear to work while diverging from the backend).
  const notifySyncFailure = useCallback(() => {
    showSnackbar({
      title: t('interfaces-matrix:WalkthroughState.SyncFailed'),
      color: 'error',
    });
  }, [showSnackbar, t]);

  /**
   * Stop the current walkthrough.
   * In RUNNER mode, gracefully sync the session as 'stopped' so the backend knows!
   * In PREVIEW/BUILDER, no session exists, so just update local state.
   * Handy for letting the user or UI say "we're done here!"
   */
  const stopWalkthrough = useCallback(async () => {
    try {
      const state = store.getState();
      const activeWalkthroughId = state.activeWalkthroughId;

      if (state.activeSession) {
        const updatedSession = {
          ...state.activeSession,
          properties: {
            ...state.activeSession.properties,
            status: 'stopped' as WalkthroughSession['status'],
          },
        };
        await saveSessionToBackend(updatedSession);
      }

      // Fire pre-end hook without awaiting so the UI tears down immediately.
      // The event is published while walkthrough state is still readable;
      // _stopWalkthrough() runs right after on the same tick.
      if (activeWalkthroughId) {
        executePreEndHook(walkthroughInstanceEventBus, activeWalkthroughId).catch(console.error);
      }

      _stopWalkthrough();
      notifyWalkthroughChange(state.settings?.targetInterfacePageId ?? '');
    } catch (error) {
      console.error('Failed to stop walkthrough session:', error);
      _stopWalkthrough();
      notifyWalkthroughChange(store.getState().settings?.targetInterfacePageId ?? '');
    }
  }, [
    store,
    _stopWalkthrough,
    saveSessionToBackend,
    notifyWalkthroughChange,
    walkthroughInstanceEventBus,
  ]);

  /**
   * Pause the current walkthrough. Super useful when the user leaves the target page!
   * RUNNER mode will persist pause-state to backend for continuity.
   */
  const pauseWalkthrough = useCallback(async () => {
    try {
      const state = store.getState();
      if (state.activeSession) {
        const updatedSession = {
          ...state.activeSession,
          properties: {
            ...state.activeSession.properties,
            status: 'paused' as WalkthroughSession['status'],
          },
        };
        await saveSessionToBackend(updatedSession);
      }
      transitionTo(WalkthroughProcessState.walkthrough_paused);
      notifyWalkthroughChange(state.settings?.targetInterfacePageId ?? '');
    } catch (error) {
      console.error('Failed to pause walkthrough session:', error);
      _stopWalkthrough();
      notifyWalkthroughChange(store.getState().settings?.targetInterfacePageId ?? '');
    }
  }, [store, transitionTo, saveSessionToBackend, _stopWalkthrough, notifyWalkthroughChange]);

  /**
   * Resume the walkthrough if paused!
   * In RUNNER mode, resume gets persisted to backend so progress feels real and lasting.
   * Otherwise, this just restores state locally—perfect for prototype flows.
   */
  const resumeWalkthrough = useCallback(async () => {
    try {
      const state = store.getState();
      const activeSession = state.activeSession;
      if (
        state.processState === WalkthroughProcessState.walkthrough_paused &&
        activeSession &&
        activeSession.properties.status === 'paused'
      ) {
        if (activeSession) {
          const updatedSession = {
            ...activeSession,
            properties: {
              ...activeSession.properties,
              status: 'in_progress' as WalkthroughSession['status'],
            },
          };
          await saveSessionToBackend(updatedSession);
        }
        transitionTo(WalkthroughProcessState.walkthrough_active);
        state.driverActions.move('next');
        notifyWalkthroughChange(state.settings?.targetInterfacePageId ?? '');
      }
    } catch (error) {
      console.error('Failed to resume walkthrough session:', error);
      _stopWalkthrough();
      notifyWalkthroughChange(store.getState().settings?.targetInterfacePageId ?? '');
    }
  }, [store, saveSessionToBackend, transitionTo, _stopWalkthrough, notifyWalkthroughChange]);

  /**
   * Complete the walkthrough!
   * In RUNNER mode, send that sweetness to the backend and end the session.
   * In PREVIEW/BUILDER, just end locally—no record kept.
   *
   * Use this to gracefully finish ("Done" button, or similar) and keep user records tidy!
   */
  const completeWalkthrough = useCallback(async (): Promise<boolean> => {
    try {
      const state = store.getState();
      const activeSession = state.activeSession;
      const activeWalkthroughId = state.activeWalkthroughId;

      if (activeSession) {
        const updatedSession = {
          ...activeSession,
          properties: {
            ...activeSession.properties,
            status: 'completed' as WalkthroughSession['status'],
            completedAt: Date.now(),
          },
        };
        const savedSession = await saveSessionToBackend(updatedSession);
        // Persistence failed: leave the session intact and report failure so the
        // caller can roll back any optimistic update and notify the user, rather
        // than tearing down a walkthrough whose completion never reached the backend.
        if (!savedSession) return false;
        setActiveSession(null);
      }

      // Fire pre-end hook without awaiting so the UI tears down immediately.
      if (activeWalkthroughId) {
        executePreEndHook(walkthroughInstanceEventBus, activeWalkthroughId).catch(console.error);
      }

      _stopWalkthrough();
      notifyWalkthroughChange(state.settings?.targetInterfacePageId ?? '');
      return true;
    } catch (error) {
      console.error('Failed to complete walkthrough session:', error);
      return false;
    }
  }, [
    store,
    _stopWalkthrough,
    saveSessionToBackend,
    setActiveSession,
    notifyWalkthroughChange,
    walkthroughInstanceEventBus,
  ]);

  /**
   * Jump to a specific step in the walkthrough by index.
   * Only updates session tracking in RUNNER mode—since, in the real world,
   * that's when progress matters!
   *
   * Super useful for custom UI navigation or deep-linking into a tour.
   */
  const goToStep = useCallback(
    async (stepIndex: number) => {
      try {
        const state = store.getState();
        if (state.processState !== WalkthroughProcessState.walkthrough_active) {
          console.error('Cannot go to step when walkthrough is not active');
          return;
        }

        const steps = state.driverState.steps;
        if (!steps || stepIndex < 0 || stepIndex >= steps.length) {
          console.error(`Invalid step index: ${stepIndex}`);
          return;
        }

        _goToStep(stepIndex);

        // Only manage session & backend sync in RUNNER mode
        if (state.activeSession) {
          const currentStep = steps[stepIndex];
          if (currentStep) {
            const updatedSession = {
              ...state.activeSession,
              properties: {
                ...state.activeSession.properties,
                currentStepId: currentStep.id,
              },
            };
            await saveSessionToBackend(updatedSession);
          }
        }
      } catch (error) {
        console.error('Failed to go to step:', error);
        _stopWalkthrough();
        notifyWalkthroughChange(store.getState().settings?.targetInterfacePageId ?? '');
      }
    },
    [store, _goToStep, saveSessionToBackend, _stopWalkthrough, notifyWalkthroughChange],
  );

  /**
   * Move to the next or previous step in the walkthrough.
   * Session updates and backend sync only apply in RUNNER mode,
   * so PREVIEW/BUILDER moves instantly with no session accountability.
   *
   * Useful for next/prev buttons or arrow key navigation.
   */
  const moveStep = useCallback(
    async (direction: 'next' | 'previous') => {
      try {
        const state = store.getState();
        if (state.processState !== WalkthroughProcessState.walkthrough_active) {
          console.error('Cannot move when walkthrough is not active');
          return;
        }

        const activeIndex = state.driverState.activeIndex;
        if (activeIndex === undefined) {
          console.error('No active step index found when moving walkthrough');
          return;
        }
        const currentStep = state.driverState.__activeStep;
        if (!currentStep) {
          console.error('No active step found when moving walkthrough');
          return;
        }
        const isLastStep = activeIndex === (state.driverState.steps?.length ?? 0) - 1;
        const activeSession: WalkthroughSessionEntity | null = state.activeSession ?? null;

        if (isLastStep && direction === 'next') {
          // Optimistically mark the final step complete so the UI reflects
          // completion immediately, then persist. completeWalkthrough() reports
          // whether the backend save landed; if it didn't, restore the pre-update
          // session (captured in `activeSession`) and notify the user so their
          // progress isn't silently lost on refresh.
          if (activeSession) {
            setActiveSession((prevSession) => {
              if (!prevSession) return null;
              const completedStepIds = new Set(prevSession.properties.completedStepIds);
              completedStepIds.add(currentStep.id);
              return {
                ...prevSession,
                properties: {
                  ...prevSession.properties,
                  currentStepId: undefined,
                  completedStepIds: Array.from(completedStepIds),
                },
              };
            });
          }
          const completed = await completeWalkthrough();
          if (!completed && activeSession) {
            setActiveSession(activeSession);
            notifySyncFailure();
          }
        } else if (direction === 'next') {
          move('next');

          if (activeSession) {
            const completedStepIds = new Set(activeSession.properties.completedStepIds);
            completedStepIds.add(currentStep.id);
            const updatedSession = {
              ...activeSession,
              properties: {
                ...activeSession.properties,
                currentStepId: currentStep.id,
                completedStepIds: Array.from(completedStepIds),
              },
            };
            const savedSession = await saveSessionToBackend(updatedSession);
            if (!savedSession) notifySyncFailure();
          }
        } else if (direction === 'previous') {
          if (activeSession) {
            const updatedSession = {
              ...activeSession,
              properties: {
                ...activeSession.properties,
                currentStepId: currentStep.id,
              },
            };
            const savedSession = await saveSessionToBackend(updatedSession);
            if (!savedSession) notifySyncFailure();
          }
          move('previous');
        }
      } catch (error) {
        console.error(`Failed to move ${direction} in walkthrough:`, error);
        _stopWalkthrough();
        notifyWalkthroughChange(store.getState().settings?.targetInterfacePageId ?? '');
      }
    },
    [
      store,
      setActiveSession,
      completeWalkthrough,
      move,
      saveSessionToBackend,
      _stopWalkthrough,
      notifyWalkthroughChange,
      notifySyncFailure,
    ],
  );

  /**
   * Prepares a walkthrough to be started (called internally).
   * Only creates a session in RUNNER mode,
   * totally sidestepping backend if you're just previewing or designing.
   * Handles fetching prior completions, session prep, and kicking off the walkthrough.
   */
  const _prepareWalkthrough = useCallback(
    async (walkthroughId: string, initialStepIndex?: number) => {
      try {
        const state = store.getState();

        if (state.processState !== WalkthroughProcessState.idle) {
          console.error('Cannot prepare walkthrough when not idle');
          return;
        }

        transitionTo(WalkthroughProcessState.preparing_walkthrough);

        const walkthrough = state.walkthroughs[walkthroughId];
        if (!walkthrough) {
          console.error(`Walkthrough with ID ${walkthroughId} not found during preparation`);
          transitionTo(WalkthroughProcessState.idle);
          return;
        }

        const walkthroughSettings = walkthrough.properties.walkthrough?.settings;
        const walkthroughSteps = walkthrough.properties.walkthrough?.steps;

        if (!walkthroughSettings || !walkthroughSteps) {
          console.error('Walkthrough is missing settings or steps');
          transitionTo(WalkthroughProcessState.idle);
          return;
        }

        const userId = state.userId;
        if (state.mode === WalkthroughMode.RUNNER && userId === undefined) {
          console.error('No user ID found when preparing walkthrough');
          transitionTo(WalkthroughProcessState.idle);
          return;
        }

        const session = await createSession({
          walkthroughId,
          interfaceId: state.instanceId,
          // we send sessionId as userId for anonymous users so that the backend can use it to identify the anon user when next time they visit the page.
          sessionId: isAnonymous ? userId : undefined,
        });
        if (!session) {
          console.error('Failed to create walkthrough session');
          transitionTo(WalkthroughProcessState.idle);
          return;
        }
        setActiveSession(session);

        prepareWalkthrough({
          walkthroughSteps,
          walkthroughId,
        });

        // Execute pre-start hook: notify the target page and wait for it
        // to finish any setup (e.g. opening a navigation drawer) before activating.
        await executePreStartHook(walkthroughInstanceEventBus, walkthroughId);

        const activationDelay = walkthroughSettings.onLoadDelay || 0;
        if (activationDelay > 0) {
          setTimeout(() => {
            activateWalkthrough(initialStepIndex);
            notifyWalkthroughChange(walkthroughSettings.targetInterfacePageId);
          }, activationDelay);
        } else {
          activateWalkthrough(initialStepIndex);
          notifyWalkthroughChange(walkthroughSettings.targetInterfacePageId);
        }
      } catch (error) {
        console.error('Failed to prepare walkthrough:', error);
        _stopWalkthrough();
      }
    },
    [
      store,
      transitionTo,
      createSession,
      setActiveSession,
      prepareWalkthrough,
      activateWalkthrough,
      _stopWalkthrough,
      notifyWalkthroughChange,
      isAnonymous,
      walkthroughInstanceEventBus,
    ],
  );

  /**
   * Internal: Finds and starts the walkthrough for the currently loaded page.
   * Only runs in RUNNER mode where on-load walkthroughs are a thing for users!
   * Will auto-start if no active walkthrough exists and process state is idle.
   */
  const _resolveWalkthroughConflicts = useCallback(
    async (pageId: string) => {
      try {
        const state = store.getState();
        if (state.activeWalkthroughId !== null) return;
        if (state.processState !== WalkthroughProcessState.idle) return;

        const walkthroughs = state.walkthroughs;

        // Find walkthrough that targets this page
        const walkthroughForPage = Object.values(walkthroughs).find(
          (walkthrough) =>
            walkthrough?.properties?.walkthrough?.settings?.targetInterfacePageId === pageId,
        );

        if (walkthroughForPage) {
          await _prepareWalkthrough(walkthroughForPage.id);
        }
      } catch (error) {
        console.error('Failed to resolve walkthrough for page:', error);
        _stopWalkthrough();
      }
    },
    [_prepareWalkthrough, _stopWalkthrough, store],
  );

  /**
   * Start a walkthrough from its id.
   * Handles all validation (idle state, page loaded, etc).
   * Creates a backend session only in RUNNER mode!
   * Great for letting users launch a tour, a checklist, or a getting started modal.
   */
  const startWalkthrough = useCallback(
    async (walkthroughId: string, initialStepIndex?: number) => {
      try {
        const state = store.getState();
        if (state.processState !== WalkthroughProcessState.idle) {
          await stopWalkthrough();
        }
        const walkthrough = state.walkthroughs[walkthroughId];
        if (!walkthrough) {
          console.error(`Walkthrough with ID ${walkthroughId} not found`);
          return;
        }
        const walkthroughSettings = walkthrough.properties.walkthrough?.settings;
        if (!walkthroughSettings) {
          console.error(`Walkthrough with ID ${walkthroughId} has no settings`);
          return;
        }
        const isTargetPageLoaded = state.loadedPageIds.includes(
          walkthroughSettings.targetInterfacePageId,
        );
        if (!isTargetPageLoaded) {
          console.error(`Target page for walkthrough ${walkthroughId} is not loaded`);
          return;
        }
        await _prepareWalkthrough(walkthroughId, initialStepIndex);
      } catch (error) {
        console.error('Failed to start walkthrough:', error);
        _stopWalkthrough();
      }
    },
    [_prepareWalkthrough, _stopWalkthrough, stopWalkthrough, store],
  );

  /**
   * Handles page load events!
   * If on the right page, try to resume or auto-start walkthroughs (RUNNER mode only).
   * Invoked automatically by routing or manual navigation.
   */
  const handlePageLoad = useCallback(
    async (pageId: string) => {
      try {
        const state = store.getState();
        // add the page to the loaded list if not already present
        if (!state.loadedPageIds.includes(pageId)) {
          store.setState((prevState) => ({
            loadedPageIds: [...prevState.loadedPageIds, pageId],
          }));
        }
        // If paused and on a walkthrough, try to resume if right page,
        // or stop it if now on the wrong page. Only relevant for RUNNER mode.
        if (state.processState === WalkthroughProcessState.walkthrough_paused) {
          const walkthroughId = state.activeWalkthroughId;
          const walkthrough = state.walkthroughs[walkthroughId ?? ''];
          if (walkthroughId && walkthrough) {
            const nextStep = state.driverState.__activeStep;
            if (nextStep && nextStep.interfacePageId === pageId) {
              await resumeWalkthrough();
            } else {
              await stopWalkthrough();
            }
          } else {
            await stopWalkthrough();
          }
        } else if (state.processState === WalkthroughProcessState.idle) {
          const pageWalkthroughsFetched = state.fetchedPageIds.includes(pageId);
          // Only resolve auto-walkthrough in runner mode,
          // where on-load onboarding is legitimate!
          if (pageWalkthroughsFetched && state.mode === WalkthroughMode.RUNNER) {
            await _resolveWalkthroughConflicts(pageId);
          } else {
            startFetchingWalkthroughs(pageId);
            // (Walkthroughs may also be fetched by other processes.)
          }
        }
      } catch (error) {
        console.error('Failed to handle page load:', error);
        _stopWalkthrough();
      }
    },
    [
      store,
      resumeWalkthrough,
      stopWalkthrough,
      _resolveWalkthroughConflicts,
      startFetchingWalkthroughs,
      _stopWalkthrough,
    ],
  );

  /**
   * Handles page unload events: removes pageId from loaded list,
   * and if it's the target of an active walkthrough (and we're on it!),
   * pauses the session (only in RUNNER mode).
   *
   * Lets walkthroughs smartly respond to context changes, like navigation away!
   */
  const handlePageUnload = useCallback(
    async (pageId: string) => {
      try {
        const state = store.getState();

        store.setState((prevState) => ({
          loadedPageIds: prevState.loadedPageIds.filter((id) => id !== pageId),
        }));

        const targetPageId = state.settings?.targetInterfacePageId;
        const isUnloadingTargetPage = targetPageId === pageId;

        const isWalkthroughActive =
          state.activeWalkthroughId &&
          isUnloadingTargetPage &&
          state.processState === WalkthroughProcessState.walkthrough_active;

        if (isWalkthroughActive) {
          await pauseWalkthrough();
        }
      } catch (error) {
        console.error('Failed to handle page unload:', error);
        _stopWalkthrough();
      }
    },
    [_stopWalkthrough, pauseWalkthrough, store],
  );

  /**
   * Handles a walkthrough being fetched for a page.
   * If appropriate, attempts to automatically start the walkthrough (RUNNER mode only!).
   * Only starts if no active walkthrough exists and process state is idle.
   * Used to kick off onboarding or suggestive tours when new content is loaded.
   */
  const handleWalkthroughsFetched = useCallback(
    async (pageId: string, walkthrough: InterfacePageEntity | undefined) => {
      try {
        const state = store.getState();

        // Store the walkthrough if it exists
        if (walkthrough) {
          store.setState((prevState) => {
            const newFetchingPageIds = prevState.fetchingPageIds.filter((id) => id !== pageId);
            const newFetchedPageIds = [...prevState.fetchedPageIds, pageId];

            return {
              walkthroughs: {
                ...prevState.walkthroughs,
                [walkthrough.id]: walkthrough,
              },
              fetchingPageIds: newFetchingPageIds,
              fetchedPageIds: newFetchedPageIds,
            };
          });
        } else {
          // Clear fetching state even if no walkthrough
          store.setState((prevState) => {
            const newFetchingPageIds = prevState.fetchingPageIds.filter((id) => id !== pageId);
            const newFetchedPageIds = [...prevState.fetchedPageIds, pageId];

            return {
              fetchingPageIds: newFetchingPageIds,
              fetchedPageIds: newFetchedPageIds,
            };
          });
        }

        // Only auto-start walkthrough if:
        // 1. Walkthrough exists
        // 2. No active walkthrough exists
        // 3. Process state is idle
        // 4. Page is loaded
        // 5. In RUNNER mode
        if (
          walkthrough &&
          state.activeWalkthroughId === null &&
          state.processState === WalkthroughProcessState.idle &&
          state.loadedPageIds.includes(pageId) &&
          state.mode === WalkthroughMode.RUNNER
        ) {
          await _prepareWalkthrough(walkthrough.id);
        }
      } catch (error) {
        console.error('Failed to mark walkthrough fetched:', error);
        _stopWalkthrough();
      }
    },
    [store, _prepareWalkthrough, _stopWalkthrough],
  );

  // Return the orchestrator methods. All are lively and ready for use!
  return {
    startWalkthrough,
    stopWalkthrough,
    pauseWalkthrough,
    resumeWalkthrough,
    completeWalkthrough,
    moveStep,
    goToStep,

    handleWalkthroughsFetched,
    handlePageLoad,
    handlePageUnload,
  };
}
